<script>
    import InlineEdit from "../../../components/inline-edit.svelte";
    import AssetsApi from "../../asset/assets.api.js";
    import { format } from "svelte-i18n";
    import { tick } from "svelte";
    import { notificate } from "../../../services/notification.js";
    import { onMount, onDestroy } from "svelte";
    import { is_ip_list, is_hostname_list } from "../../asset/assets.api.js";
    import { locale } from "svelte-i18n";

    let { asset = {}, navigation = false } = $props();
    let tooltipID = $state();
    let in_scope_checkbox = $derived.by(() => asset.in_scope);
    let auto_delete_checkbox = $derived.by(() => asset.auto_delete);
    let inputElement = $state();
    let autoDeleteInputElement = $state();

    // State for additional properties
    let extraProperties = $state({
        port_status: [],
        os: "",
        location: "",
        netbios: [],
        expiration_date: "",
        rels: [],
        tag: [],
        finding_count: null,
    });
    let loadingProperties = $state(false);
    let propertiesError = $state(null);
    let modalElement = $state(null);

    let showHelp = $state({
        persistent: false,
        mouseover: false,
    });

    // Function to fetch additional properties for the asset
    const fetchAdditionalProperties = async () => {
        // Don't fetch if no asset ID/kind
        if (!asset.id || !asset.kind) {
            return;
        }

        // Check if we already have properties (simple check)
        if (
            extraProperties.port_status.length > 0 ||
            extraProperties.os ||
            extraProperties.location
        ) {
            return;
        }

        try {
            loadingProperties = true;
            propertiesError = null;

            const assetData = await AssetsApi.findAsset(asset.id);
            asset.finding_count = assetData.finding_count;

            // Fetch properties based on asset kind
            const properties = await fetchAssetProperties(asset.kind, asset.id);

            // Update the extraProperties state individually to ensure reactivity
            if (properties.port_status !== undefined) {
                extraProperties.port_status = structuredClone(
                    properties.port_status,
                );
            }
            if (properties.os !== undefined) {
                console.log(properties.os, "os");
                extraProperties.os = structuredClone(properties.os);
            }
            if (properties.location !== undefined) {
                extraProperties.location = structuredClone(properties.location);
            }
            if (properties.netbios !== undefined) {
                extraProperties.netbios = structuredClone(
                    properties.netbios,
                );
            }
            if (properties.expiration_date !== undefined) {
                console.log(properties.expiration_date)
                extraProperties.expiration_date = structuredClone(
                    properties.expiration_date,
                );
            }
            if (properties.rels !== undefined) {
                extraProperties.rels = structuredClone(properties.rels);
            }
        } catch (error) {
            propertiesError = "Failed to load additional asset properties";
        } finally {
            loadingProperties = false;
        }
    };

    // Export function to manually trigger property fetch
    export const triggerPropertyFetch = async () => {
        await fetchAdditionalProperties();
    };

    // Set up modal event listener
    onMount(() => {
        // Trigger property fetch when component mounts
        // This will happen when the modal opens
        fetchAdditionalProperties();

        // Also listen for custom event from parent component as fallback
        window.addEventListener("assetModalOpened", handleAssetModalOpened);
    });

    // Handle custom event from parent component
    const handleAssetModalOpened = async (event) => {
        if (event.detail.assetId === asset.id) {
            await fetchAdditionalProperties();
        }
    };

    // Clean up event listener
    onDestroy(() => {
        // Remove custom event listener
        window.removeEventListener("assetModalOpened", handleAssetModalOpened);
    });

    // Function to fetch asset properties based on kind
    const fetchAssetProperties = async (kind, assetId) => {
        const properties = {};

        // Determine what properties to fetch based on asset kind
        if (is_ip_list(kind)) {
            // Fetch port status
            try {
                const portStatusResponse =
                    await AssetsApi.fetchAssetsProperties({
                        filter: {
                            kind: "port_status",
                            asset_id: assetId,
                        },
                    });
                properties.port_status = portStatusResponse;
            } catch (error) {
                console.warn("Could not fetch port status:", error);
            }

            // Fetch OS
            try {
                const osResponse = await AssetsApi.fetchAssetsProperties({
                    filter: {
                        kind: "os",
                        name: "name",
                        asset_id: assetId,
                    },
                });
                if (osResponse && osResponse.length > 0) {
                    properties.os = osResponse[0].value;
                }
            } catch (error) {
                console.warn("Could not fetch OS:", error);
            }

            // Fetch location for public IPs
            if (is_ip_list(kind, true).public) {
                try {
                    const locationResponse =
                        await AssetsApi.fetchAssetsProperties({
                            filter: {
                                kind: "location",
                                asset_id: assetId,
                            },
                        });

                    properties.location = locationResponse[0].value || "";
                } catch (error) {
                    console.warn("Could not fetch location:", error);
                }
            }

            // Fetch NetBIOS for private IPs
            if (is_ip_list(kind, true).private) {
                try {
                    const netbiosResponse =
                        await AssetsApi.fetchAssetsProperties({
                            filter: {
                                kind: "netbios",
                                asset_id: assetId,
                            },
                        });
                    if (netbiosResponse && netbiosResponse.length > 0) {
                        properties.netbios = netbiosResponse;
                    }
                } catch (error) {
                    console.warn("Could not fetch NetBIOS:", error);
                }
            }
        }

        // Fetch domain properties
        if (kind.includes("domain")) {
            try {
                const dateResponse = await AssetsApi.fetchAssetsProperties({
                    filter: {
                        kind: "date",
                        asset_id: assetId,
                    },
                });
                if (dateResponse && dateResponse.length > 0) {
                    properties.expiration_date = dateResponse[0].value;
                }
            } catch (error) {
                console.warn("Could not fetch domain dates:", error);
            }
        }

        // Fetch relations for hostname assets
        if (is_hostname_list(kind)) {
            try {
                const relationsResponse = await AssetsApi.fetchAssetsRelations({
                    filter: {
                        kind: "dns",
                        from_asset_id: assetId,
                    },
                });
                properties.rels = relationsResponse;
            } catch (error) {
                console.warn("Could not fetch relations:", error);
            } finally {
                for (let rel of properties.rels) {
                    const related = await AssetsApi.findAsset(rel.to_asset_id);
                    rel.name = related.name;
                    rel.related_kind = related.kind;
                }
            }
        }
        if (is_ip_list(kind)) {
            try {
                const relationsResponse = await AssetsApi.fetchAssetsRelations({
                    filter: {
                        kind: "dns",
                        to_asset_id: assetId,
                    },
                });
                properties.rels = relationsResponse;
            } catch (error) {
                console.warn("Could not fetch relations:", error);
            } finally {
                for (let rel of properties.rels) {
                    const related = await AssetsApi.findAsset(
                        rel.from_asset_id,
                    );
                    rel.name = related.name;
                    rel.related_kind = related.kind;
                }
            }
        }
        return properties;
    };

    const handleSaveFn = (data, attribute) => {
        return ({ detail: input }) => {
            let newAsset = {
                id: data.id,
            };
            if (attribute == "in_scope") {
                newAsset.in_scope = !in_scope_checkbox;
            } else if (attribute == "auto_delete") {
                newAsset.auto_delete = !auto_delete_checkbox;
            } else {
                newAsset[attribute] = input.value;
            }

            AssetsApi.saveAsset(newAsset, {}, false)
                .then(async (response) => {
                    if (!response.ok) {
                        let errors = await response.json();
                        // api validation errors
                        if (response.status == 401) {
                            notificate("limitReached");
                        } else {
                            let toastConfig = {
                                data: data.name,
                                options: {
                                    timeOut: "0",
                                    extendedTimeOut: "0",
                                    showDuration: "0",
                                    hideDuration: "0",
                                    closeButton: true,
                                },
                            };
                            if (config && config.massive_addition)
                                toastConfig.error = errors;
                            notificate("save_error", "toast", toastConfig);
                        }
                        throw errors;
                    } else {
                        return response.json();
                    }
                })
                .then((pAsset) => {
                    if (newAsset.in_scope && !pAsset.in_scope) {
                        notificate("limitReached");
                    } else {
                        notificate("save_ok", "toast", { data: pAsset.name });
                    }
                    asset = pAsset;
                })
                .catch(() => {
                    return false;
                });
        };
    };

    const handleCopy = async (str, tooltip) => {
        copyText(str, tooltip);
    };

    const copyText = async (str, tooltip) => {
        await tick();
        if ("clipboard" in navigator) {
            navigator.clipboard
                .writeText(str)
                .then(() => {
                    showTooltip(tooltip);
                    hideTooltip(tooltip);
                })
                .catch((err) => console.error(err.name, err.message));
        } else {
            let selBox = document.createElement("textarea");
            selBox.style.position = "fixed";
            selBox.style.left = "0";
            selBox.style.top = "0";
            selBox.style.opacity = "0";
            selBox.value = str;
            document.body.appendChild(selBox);
            selBox.focus();
            selBox.select();
            // @TODO: upgrade to clipboard API
            try {
                const success = document.execCommand("copy");
            } catch (err) {
                return false;
            }
            showTooltip(tooltip);
            document.body.removeChild(selBox);
            hideTooltip(tooltip);
        }
    };

    const hideTooltip = (tooltip) => {
        setTimeout(() => {
            tooltip.classList.add("trigger-opacity-cero");
            tooltip.classList.remove("trigger-opacity-one");
            setTimeout(() => tooltip.classList.add("d-none"), 1000);
        }, 2000);
    };

    const showTooltip = (tooltip) => {
        tooltip.classList.remove("d-none");
        setTimeout(() => {
            tooltip.classList.remove("trigger-opacity-cero");
            tooltip.classList.add("trigger-opacity-one");
        }, 1000);
    };

    // InlineEdit options
    let inlineEdit = {
        type: "text",
        placeholder: $format("add_desc"),
        lang: {
            edit: $format("buttons.edit"),
            save: $format("buttons.save"),
            cancel: $format("buttons.cancel"),
        },
    };
</script>

{#if JSON.stringify($state.snapshot(asset)) != "{}"}
    <!-- Loading indicator for properties -->
    {#if loadingProperties}
        <div class="d-flex flex-container-2item mt-3">
            <div>
                <div><strong>Loading additional properties...</strong></div>
                <div><i class="fas fa-sync-alt fa-spin"></i></div>
            </div>
        </div>
    {/if}

    <!-- Error message for properties -->
    {#if propertiesError}
        <div class="d-flex flex-container-2item mt-3">
            <div>
                <div><strong>Error loading properties</strong></div>
                <div>{propertiesError}</div>
            </div>
        </div>
    {/if}

    <div class="d-flex flex-container-2item">
        <div>
            <div><strong>{$format("list.columns.name")}</strong></div>
            {#if asset.name}
                {#if navigation}
                    <a href={`/asset/${asset.kind}/${asset.id}/`}
                        >{asset.nickname || asset.name || ""}</a
                    >
                {:else}
                    {asset.nickname || asset.name || ""}
                {/if}
            {:else}
                <div class="overlay justify-content-start">
                    <i class="fas fa-sync-alt fa-spin"></i>
                </div>
            {/if}
        </div>
        <div class="">
            <div><strong>{$format("list.columns.description")}</strong></div>
            {#if asset.hasOwnProperty("description")}
                <InlineEdit
                    {...inlineEdit}
                    value={asset.description}
                    on:save={handleSaveFn(asset, "description")}
                />
            {:else}
                <div class="overlay justify-content-start">
                    <i class="fas fa-sync-alt fa-spin"></i>
                </div>
            {/if}
        </div>
    </div>
    <div class="d-flex flex-container-2item mt-3">
        <div class="mt-3">
            <div><strong>{$format("list.columns.in_scope")}</strong></div>
            {#if asset.hasOwnProperty("in_scope")}
                {#if asset && !asset.in_scope && asset.third_party}
                    <i class="fa fa-eye-slash" style="color: rgb(102, 102, 102) !important;"></i> <i class="fa fa-cloud" style="color: rgb(102, 102, 102) !important;"></i>
                {:else}
                    <div class="form-check form-switch">
                        <input
                            type="checkbox"
                            bind:this={inputElement}
                            bind:checked={in_scope_checkbox}
                            onclick={handleSaveFn(asset, "in_scope")}
                            class="form-check-input"
                            id={"switch_in_scope" + asset.id}
                        />
                        <label
                            class="custom-control-label"
                            for={"switch_in_scope" + asset.id}
                        ></label>
                    </div>
                {/if}
            {:else}
                <div class="overlay justify-content-start">
                    <i class="fas fa-sync-alt fa-spin"></i>
                </div>
            {/if}
        </div>
        <div class="mt-3">
            {#if asset.hasOwnProperty("third_party") && asset.third_party}
                <div style="position: relative; max-width: 10em;">
                    <strong
                        >{$format(
                            "page.asset.form.warnings.managed_by",
                        )}</strong
                    >
                    <i
                        class="ms-2 mt-2 icon-help help-btn far fa-question-circle"
                        style="color: rgb(102, 102, 102) !important;"
                        class:showHelp={showHelp.persistent ||
                            showHelp.mouseover}
                        onclick={() =>
                            (showHelp.persistent = !showHelp.persistent)}
                        onmouseover={() =>
                            (showHelp.mouseover = !showHelp.mouseover)}
                        onfocus={() =>
                            (showHelp.mouseover = !showHelp.mouseover)}
                        onmouseleave={() =>
                            (showHelp.mouseover = !showHelp.mouseover)}
                    ></i>
                    <div>
                        {$format("page.asset.form.warnings.third_party_detail")}
                    </div>
                </div>

                <div>{asset.third_party}</div>
            {/if}
        </div>
    </div>
    <div class="d-flex align-items-center flex-container-2item mt-3">
        <div>
            {#key asset.auto_delete}<div class="d-flex">
                    <strong style="max-width: fit-content;"
                        >{$format("asset.auto_delete_title")}</strong
                    ><i
                        style="top: -5px !important; color: rgb(102, 102, 102) !important;"
                        title={$format("asset.auto_delete_helper")}
                        class="ms-2 mt-2 icon-help help-btn far fa-question-circle"
                    ></i>
                </div>{/key}
            {#if asset.hasOwnProperty("auto_delete")}
                {#if asset.kind != "domain" && !asset.kind.includes("network")}
                    <div class="form-check form-switch">
                        <input
                            type="checkbox"
                            bind:this={autoDeleteInputElement}
                            bind:checked={auto_delete_checkbox}
                            onclick={handleSaveFn(asset, "auto_delete")}
                            class="form-check-input"
                            id={"switch_auto_delete" + asset.id}
                        />
                        <label
                            class="custom-control-label"
                            for={"switch_auto_delete" + asset.id}
                        ></label>
                    </div>
                {:else}
                    <div>{$format("asset.manual_delete")}</div>
                {/if}
            {/if}
        </div>
        <div>
            <div><strong>{$format("list.columns.is_dynamic")}</strong></div>
            {#if !asset.is_dynamic}
                <div class="d-flex align-items-center">
                    {$format("asset.is_not_dynamic")}&nbsp;<i
                        title={$format("asset.is_not_dynamic")}
                        class="fas fa-user"
                        style="margin-bottom: 3px !important; color: rgb(102, 102, 102) !important;"
                    ></i>
                </div>
            {:else}
                <div class="d-flex align-items-center">
                    {$format("asset.is_dynamic")}&nbsp;<i
                        title={$format("asset.is_dynamic")}
                        class="fas fa-cogs"
                        style="color: rgb(102, 102, 102) !important;"
                    ></i>
                </div>
            {/if}
        </div>
    </div>
    <div class="d-flex flex-container-2item mt-3">
        <div>
            <div><strong>ID</strong></div>
            {#if asset.id}
                {asset.id || ""}
                <button
                    class="btn btn-copy far fa-copy"
                    title="Copiar al portapapeles"
                    aria-label="Copiar"
                    style="position: relative; top: -2px; color: rgb(102,102,102) !important; color: rgb(102, 102, 102) !important;"
                    onclick={() => handleCopy(asset.id, tooltipID)}
                ></button>
                <span
                    id="tooltip-compose"
                    class="d-none trigger-opacity-cero copy-tooltip"
                    bind:this={tooltipID}>{$format("copied")}</span
                >
            {:else}
                <div class="overlay justify-content-start">
                    <i class="fas fa-sync-alt fa-spin"></i>
                </div>
            {/if}
        </div>
    </div>

    <!-- Loading indicator for properties -->
    {#if loadingProperties}
        <div class="d-flex flex-container-2item mt-3">
            <div>
                <div><strong>Loading additional properties...</strong></div>
                <div><i class="fas fa-sync-alt fa-spin"></i></div>
            </div>
        </div>
    {/if}

    <!-- Error message for properties -->
    {#if propertiesError}
        <div class="d-flex flex-container-2item mt-3">
            <div>
                <div><strong>Error loading properties</strong></div>
                <div>{propertiesError}</div>
            </div>
        </div>
    {/if}

    {#if extraProperties.port_status && extraProperties.port_status.length > 0}
        <div class="d-flex flex-container-2item mt-3">
            <div>
                <div><strong>{$format("list.columns.ports")}</strong></div>
                <div>
                    {#each extraProperties.port_status as port}
                        {#if port}
                            <span class="badge bg-secondary me-1 mb-1"
                                >{port.name}</span
                            >
                        {/if}
                    {/each}
                </div>
            </div>
        </div>
    {/if}

    {#if extraProperties.os && extraProperties.os !== ""}
        <div class="d-flex flex-container-2item mt-3">
            <div>
                <div><strong>{$locale == "es" ? "Sistema Operativo" : "Operative System"}</strong></div>
                <div>
                    {#if AssetsApi.os_icons.find((icon) =>extraProperties.os.toLowerCase().replaceAll(" ", "").includes(icon.label))}
                        <div class="d-flex align-items-center"> {extraProperties.os} <img alt="" title="{extraProperties.os}" src="{`/img/${AssetsApi.os_icons.find((icon) =>extraProperties.os.toLowerCase().replaceAll(" ", "").includes(icon.label)).fileName}`}" style="{`height: ${AssetsApi.os_icons.find((icon) =>extraProperties.os.toLowerCase().replaceAll(" ", "").includes(icon.label)).height}px; max-width: ${AssetsApi.os_icons.find((icon) =>extraProperties.os.toLowerCase().replaceAll(" ", "").includes(icon.label)).height}px; margin-left: 5px;`}" /></div>
                    {:else}
                        <div class="d-flex align-items-center"> {extraProperties.os}<i title="{extraProperties.os}" class='fas fa-tools' style="color: rgb(102,102,102) !important; max-width: 24px !important;"></i></div>
                    {/if}
                </div>
            </div>
        </div>
    {/if}

    {#if extraProperties.netbios && extraProperties.netbios.length > 0}
        <div class="d-flex flex-container-2item mt-3">
            <div>
                <div><strong>Hostname</strong></div>
                <div>
                    {#each extraProperties.netbios as host}
                        {#if host}
                            <span class="badge bg-secondary me-1 mb-1"
                                >{host.value}</span
                            >
                        {/if}
                    {/each}
                </div>
            </div>
        </div>
    {/if}

    {#if extraProperties.expiration_date && extraProperties.expiration_date !== ""}
        <div class="d-flex flex-container-2item mt-3">
            <div>
                <div><strong>{$format("list.columns.valid_until")}</strong></div>
                <div>
                    {extraProperties.expiration_date.split(" ")[0]}
                </div>
            </div>
        </div>
    {/if}

    {#if extraProperties.rels && extraProperties.rels.length > 0}
        <div class="d-flex flex-container-2item mt-3">
            <div>
                <div><strong>{is_hostname_list(asset.kind) ? "IPs" : "Hostnames"}</strong></div>
                <div class="d-flex">
                    {#each extraProperties.rels as rel}
                        <span
                            class="badge bg-secondary d-flex align-items-center me-1"
                            style="gap: 5px; max-width: fit-content;"
                            >{rel.name}
                            <i
                                class={"fa-xs " +
                                    AssetsApi.AssetsIcons[rel.related_kind]}
                                style="color: white;"
                            ></i></span
                        >
                    {/each}
                </div>
            </div>
        </div>
    {/if}

    {#if extraProperties.location}
        <div class="d-flex flex-container-2item mt-3">
            <div>
                <div><strong>{$format("list.columns.country")}</strong></div>
                <div title="{extraProperties.location}">
                    <i class="{`flag-icon flag-icon-${extraProperties.location.toLowerCase()}`}"></i>
                </div>
            </div>
        </div>
    {/if}

    {#if asset.finding_count}
        <div class="d-flex flex-container-2item mt-3">
            <div>
                <div><strong>{$format("list.columns.findings")}</strong></div>
                {#if asset.finding_count.critical.open + asset.finding_count.critical.accepted > 0}
                    <div
                        class="cvss-badge cvss-badge-small cvss-critical"
                        title="Crítica"
                    >
                        {asset.finding_count.critical.open +
                            asset.finding_count.critical.accepted}
                    </div>
                {:else}
                    <div
                        class="cvss-badge cvss-badge-small cvss-zero"
                        title="Crítica"
                    >
                        {asset.finding_count.critical.open +
                            asset.finding_count.critical.accepted}
                    </div>
                {/if}
                {#if asset.finding_count.high.open + asset.finding_count.high.accepted > 0 || asset.finding_count.critical.open + asset.finding_count.critical.accepted > 0}
                    <div
                        class="cvss-badge cvss-badge-small cvss-high"
                        title="Alta"
                    >
                        {asset.finding_count.high.open +
                            asset.finding_count.high.accepted}
                    </div>
                {:else}
                    <div
                        class="cvss-badge cvss-badge-small cvss-zero"
                        title="Alta"
                    >
                        {asset.finding_count.high.open +
                            asset.finding_count.high.accepted}
                    </div>
                {/if}
                {#if asset.finding_count.medium.open + asset.finding_count.medium.accepted > 0 || asset.finding_count.high.open + asset.finding_count.high.accepted > 0 || asset.finding_count.critical.open + asset.finding_count.critical.accepted > 0}
                    <div
                        class="cvss-badge cvss-badge-small cvss-medium"
                        title="Media"
                    >
                        {asset.finding_count.medium.open +
                            asset.finding_count.medium.accepted}
                    </div>
                {:else}
                    <div
                        class="cvss-badge cvss-badge-small cvss-zero"
                        title="Media"
                    >
                        {asset.finding_count.medium.open +
                            asset.finding_count.medium.accepted}
                    </div>
                {/if}
                {#if asset.finding_count.low.open + asset.finding_count.low.accepted > 0 || asset.finding_count.medium.open + asset.finding_count.medium.accepted > 0 || asset.finding_count.high.open + asset.finding_count.high.accepted > 0 || asset.finding_count.critical.open + asset.finding_count.critical.accepted > 0}
                    <div
                        class="cvss-badge cvss-badge-small cvss-low"
                        title="Baja"
                    >
                        {asset.finding_count.low.open +
                            asset.finding_count.low.accepted}
                    </div>
                {:else}
                    <div
                        class="cvss-badge cvss-badge-small cvss-zero"
                        title="Baja"
                    >
                        {asset.finding_count.low.open +
                            asset.finding_count.low.accepted}
                    </div>
                {/if}
            </div>
        </div>
    {/if}
{:else}
    <div class="card-body">
        <div class="d-flex flex-container-2item">
            <div>{$format("notifications.resource_not_found")}</div>
        </div>
    </div>
{/if}

<style>
    .d-flex > * {
        flex: 1;
    }

    .d-flex > i.icon-help,
    .d-flex > i.fas.fa-cogs {
        flex: 0;
    }
    .flex-container-2item div:first-child {
        flex: 2;
    }

    .help-btn {
        position: relative;
        top: -3px;
    }

    .help-btn + div {
        display: none;
        position: absolute;
        background: rgb(0, 0, 0, 0.8);
        border-radius: 5px;
        color: white;
        padding: 15px;
        top: -83px;
        right: -17px;
        width: 410px;
    }

    .help-btn + div:after {
        content: "";
        width: 0;
        height: 0;
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-top: 10px solid rgba(0, 0, 0, 0.8);
        position: absolute;
        bottom: -7px;
        right: 16px;
    }

    .help-btn.showHelp + div {
        display: block;
    }

    button::before {
        color: rgba(102, 102, 102) !important;
    }
</style>
